/**
 * InvestmentDetail — szczegóły żywej inwestycji (Pipeline B, 2026-05-14).
 *
 * 5 tabów:
 *   📋 Przegląd — info + łańcuch decyzyjny (DecisionChainPanel reuse z Tender)
 *   📨 Outreach — log kontaktów per rola
 *   📐 Spec-in — produkty wpisane w specyfikację
 *   🔗 Powiązany przetarg — heurystyczny matching gdy zostanie ogłoszony
 *   📜 Historia — timeline + stage changes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  TrendingUp, ArrowLeft, MapPin, Calendar, Building2, Loader2,
  Users, Sparkles, Trophy, X as XIcon, CheckCircle2, Plus,
  Mail, Phone, Linkedin, ArrowRight, MessageSquare, FileText,
  AlertCircle, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getInvestment, changeInvestmentStage,
  logSpecIn, removeSpecIn, logOutreach,
  findLinkedTender, linkInvestmentTender,
} from '../lib/api';
import DecisionChainPanel from '../components/tenders/DecisionChainPanel';
import PlanLock from '../components/common/PlanLock';
import ActivityFeed from '../components/activity/ActivityFeed';
import TimelineView from '../components/activity/TimelineView';


const STAGES = [
  'discovered', 'researched', 'contacted', 'spec_in', 'tracked', 'won', 'lost',
];

const STAGE_LABELS = {
  discovered:  { label: 'Wykryte', color: 'bg-zinc-500/15 text-zinc-300' },
  researched:  { label: 'Zbadane', color: 'bg-blue-500/15 text-blue-300' },
  contacted:   { label: 'Kontakt', color: 'bg-cyan-500/15 text-cyan-300' },
  spec_in:     { label: 'Spec-in', color: 'bg-primary/15 text-primary' },
  tracked:     { label: 'Tracked', color: 'bg-amber-500/15 text-amber-300' },
  won:         { label: 'Wygrane 🏆', color: 'bg-emerald-500/15 text-emerald-300' },
  lost:        { label: 'Przegrane ✗', color: 'bg-red-500/15 text-red-300' },
};

const ROLES = [
  { key: 'investor',              label: 'Inwestor' },
  { key: 'architect',             label: 'Architekt' },
  { key: 'lighting_designer',     label: 'Projektant oświetlenia ⭐' },
  { key: 'general_contractor',    label: 'Generalny wykonawca' },
  { key: 'electrical_contractor', label: 'Wykonawca elektryczny' },
  { key: 'procurement_contact',   label: 'Kontakt zamówienia' },
];


function fmtPLN(v) {
  if (!v || v === 0) return '—';
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 }).format(v) + ' PLN';
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return s.slice(0, 10);
  }
}

function fmtDateTime(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return s.slice(0, 16);
  }
}


// ============================================================
// Spec-in tab
// ============================================================

function SpecInTab({ investmentId, specced, onChanged, lang = 'pl' }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    product_name: '',
    product_id: '',
    specced_by: '',
    confidence: 0.7,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.product_name.trim()) {
      toast.error('Nazwa produktu wymagana');
      return;
    }
    setSaving(true);
    try {
      await logSpecIn(investmentId, form);
      toast.success('Zalogowano spec-in');
      setOpen(false);
      setForm({ product_name: '', product_id: '', specced_by: '', confidence: 0.7, notes: '' });
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(p) {
    if (!window.confirm(`Usunąć "${p.product_name}" z spec-in?`)) return;
    try {
      await removeSpecIn(investmentId, p.product_id || p.product_name);
      onChanged && onChanged();
    } catch (err) {
      toast.error('Błąd usuwania');
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-primary" />
            Spec-in tracking
          </CardTitle>
          <Button onClick={() => setOpen(true)} size="sm" className="gap-1">
            <Plus className="w-3 h-3" />
            Dodaj produkt
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Produkty wpisane przez projektanta oświetlenia w specyfikację techniczną.
          Confidence = jak pewnie ta pozycja jest "nasza" (po rozmowie z projektantem).
          Po dodaniu pierwszego produktu, etap inwestycji automatycznie przechodzi na "spec_in".
        </p>

        {(!specced || specced.length === 0) ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
            Brak wpisanych produktów. Po skontaktowaniu się z projektantem dodaj tu nasze produkty.
          </div>
        ) : (
          <div className="space-y-2">
            {specced.map((p, i) => (
              <div key={i} className="border border-border rounded-md p-3 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{p.product_name}</div>
                  <div className="text-xs text-muted-foreground space-y-0.5 mt-1">
                    {p.product_id && <div>ID: <code className="text-primary">{p.product_id}</code></div>}
                    {p.specced_by && <div>Wpisany przez: <strong>{p.specced_by}</strong></div>}
                    <div className="flex items-center gap-2">
                      <span>Confidence:</span>
                      <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary"
                          style={{ width: `${(p.confidence || 0) * 100}%` }}
                        />
                      </div>
                      <strong>{Math.round((p.confidence || 0) * 100)}%</strong>
                    </div>
                    {p.notes && <p className="italic text-[11px]">{p.notes}</p>}
                    {p.at && <div className="text-[10px]">{fmtDateTime(p.at)}</div>}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(p)}
                  className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wpisz produkt w specyfikację</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nazwa produktu *</Label>
              <Input
                value={form.product_name}
                onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                placeholder="np. nazwa Twojego produktu / modelu"
              />
            </div>
            <div>
              <Label className="text-xs">ID produktu (z naszej bazy, opcjonalnie)</Label>
              <Input
                value={form.product_id}
                onChange={(e) => setForm({ ...form, product_id: e.target.value })}
                placeholder="np. LENA-OPT-50-5K"
              />
            </div>
            <div>
              <Label className="text-xs">Kto wpisał (imię projektanta)</Label>
              <Input
                value={form.specced_by}
                onChange={(e) => setForm({ ...form, specced_by: e.target.value })}
                placeholder="np. Marek Nowak (projektant)"
              />
            </div>
            <div>
              <Label className="text-xs">Confidence: {Math.round(form.confidence * 100)}%</Label>
              <Input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={form.confidence}
                onChange={(e) => setForm({ ...form, confidence: parseFloat(e.target.value) })}
              />
              <div className="text-[10px] text-muted-foreground mt-0.5">
                30% = potwierdzenie ustne · 60% = email · 90% = zobaczyłem w specyfikacji
              </div>
            </div>
            <div>
              <Label className="text-xs">Notatki</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="np. Marek powiedział że jest w spec w wersji v2 z 10.05"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anuluj</Button>
            <Button onClick={save} disabled={saving} className="gap-1">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
              Dodaj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ============================================================
// Outreach tab
// ============================================================

function OutreachTab({ investmentId, outreach, onChanged, lang = 'pl' }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    role: 'lighting_designer',
    contact_name: '',
    contact_email: '',
    channel: 'email',
    subject: '',
    summary: '',
    response_received: false,
    response_summary: '',
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.role) return;
    setSaving(true);
    try {
      await logOutreach(investmentId, form);
      toast.success('Outreach zalogowany');
      setOpen(false);
      setForm({
        role: 'lighting_designer', contact_name: '', contact_email: '',
        channel: 'email', subject: '', summary: '',
        response_received: false, response_summary: '',
      });
      onChanged && onChanged();
    } catch (err) {
      toast.error('Błąd zapisu');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Send className="w-4 h-4 text-primary" />
            Outreach pipeline
          </CardTitle>
          <Button onClick={() => setOpen(true)} size="sm" className="gap-1">
            <Plus className="w-3 h-3" />
            Loguj kontakt
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Każde wysłanie maila / telefon / spotkanie z rolą decyzyjną. Po pierwszym kontakcie etap inwestycji
          automatycznie przechodzi na "contacted". Klucz: <strong>projektant oświetlenia</strong> — to on
          wpisuje produkty w specyfikację.
        </p>

        {(!outreach || outreach.length === 0) ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
            Brak zalogowanych kontaktów. Pierwszy zwykle: znajdź w Łańcuchu projektanta oświetlenia → klik "Loguj kontakt".
          </div>
        ) : (
          <div className="space-y-2">
            {outreach.map((o) => {
              const roleLabel = (ROLES.find((r) => r.key === o.role) || {}).label || o.role;
              const ChannelIcon = o.channel === 'phone' ? Phone : o.channel === 'linkedin' ? Linkedin : Mail;
              return (
                <div key={o.id} className="border border-border rounded-md p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{roleLabel}</Badge>
                      <Badge variant="outline" className="text-[10px] gap-1">
                        <ChannelIcon className="w-2.5 h-2.5" />
                        {o.channel}
                      </Badge>
                      {o.response_received ? (
                        <Badge className="bg-emerald-500/15 text-emerald-300 text-[10px]">
                          ↩ Odpowiedział
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-amber-300">
                          ⏳ Czeka na odpowiedź
                        </Badge>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {fmtDateTime(o.created_at)}
                    </span>
                  </div>
                  <div className="text-sm">
                    <strong>{o.contact_name || '—'}</strong>
                    {o.contact_email && <span className="text-muted-foreground"> · {o.contact_email}</span>}
                  </div>
                  {o.subject && (
                    <div className="text-xs text-muted-foreground mt-1">
                      <strong>Temat:</strong> {o.subject}
                    </div>
                  )}
                  {o.summary && (
                    <div className="text-xs text-muted-foreground mt-1">
                      <strong>Podsumowanie:</strong> {o.summary}
                    </div>
                  )}
                  {o.response_received && o.response_summary && (
                    <div className="text-xs mt-1 p-2 bg-emerald-500/10 rounded">
                      <strong>Odpowiedź:</strong> {o.response_summary}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Loguj kontakt z rolą decyzyjną</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Rola decyzyjna</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Imię kontaktu</Label>
                <Input
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="np. Marek Nowak"
                />
              </div>
              <div>
                <Label className="text-xs">Email kontaktu</Label>
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  placeholder="marek@studio.pl"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Kanał</Label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">📧 Email</SelectItem>
                  <SelectItem value="phone">📞 Telefon</SelectItem>
                  <SelectItem value="linkedin">💼 LinkedIn</SelectItem>
                  <SelectItem value="in-person">🤝 Osobiście</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Temat / kontekst</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="np. Prezentacja oferty na hotel"
              />
            </div>
            <div>
              <Label className="text-xs">Podsumowanie rozmowy</Label>
              <Textarea
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                rows={2}
                placeholder="O czym rozmawiałem, co zaproponowałem"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="resp-received"
                checked={form.response_received}
                onChange={(e) => setForm({ ...form, response_received: e.target.checked })}
              />
              <Label htmlFor="resp-received" className="text-xs cursor-pointer">
                Otrzymałem odpowiedź
              </Label>
            </div>
            {form.response_received && (
              <div>
                <Label className="text-xs">Co odpowiedział</Label>
                <Textarea
                  value={form.response_summary}
                  onChange={(e) => setForm({ ...form, response_summary: e.target.value })}
                  rows={2}
                  placeholder="np. Wstępnie zainteresowany, prosi o cennik na 100 sztuk"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anuluj</Button>
            <Button onClick={save} disabled={saving} className="gap-1">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ============================================================
// Linked Tender tab
// ============================================================

function LinkedTenderTab({ investmentId, linkedTender, onChanged, lang = 'pl' }) {
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [linking, setLinking] = useState(false);
  const navigate = useNavigate();

  async function searchCandidates() {
    setSearching(true);
    try {
      const r = await findLinkedTender(investmentId);
      setCandidates(r.candidates || []);
      if (r.candidates?.length === 0) {
        toast.info('Brak pasujących tenderów');
      } else {
        toast.success(`Znaleziono ${r.candidates.length} kandydatów`);
      }
    } catch (err) {
      toast.error('Błąd wyszukiwania');
    } finally {
      setSearching(false);
    }
  }

  async function link(tenderSourceId) {
    setLinking(true);
    try {
      await linkInvestmentTender(investmentId, tenderSourceId);
      toast.success('Połączono inwestycję z tenderem');
      onChanged && onChanged();
    } catch (err) {
      toast.error('Błąd');
    } finally {
      setLinking(false);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <ArrowRight className="w-4 h-4 text-primary" />
          Powiązany przetarg
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Gdy ta inwestycja zostanie ogłoszona jako przetarg BZP/TED, połącz ją tu. System przeniesie
          stage na "tracked" i pokaże historię outreachu w widoku tendera.
        </p>

        {linkedTender ? (
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-md p-3 space-y-1">
            <Badge className="bg-emerald-500/15 text-emerald-300 text-[10px] gap-1">
              <CheckCircle2 className="w-2.5 h-2.5" />
              POŁĄCZONE
            </Badge>
            <div className="font-medium text-sm">{linkedTender.title}</div>
            <div className="text-xs text-muted-foreground space-y-0.5">
              <div>Źródło: {linkedTender.source?.toUpperCase()}</div>
              <div>Termin: {fmtDate(linkedTender.deadline)}</div>
              {linkedTender.estimated_value > 0 && <div>Wartość: {fmtPLN(linkedTender.estimated_value)}</div>}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/tenders/${encodeURIComponent(linkedTender.source_id)}`)}
              className="mt-2 gap-1"
            >
              Otwórz tender
              <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Button
              onClick={searchCandidates}
              disabled={searching}
              variant="outline"
              size="sm"
              className="gap-1"
            >
              {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Znajdź pasujące przetargi (auto)
            </Button>

            {candidates !== null && candidates.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-4">
                Brak kandydatów. Spróbuj za jakiś czas — gdy inwestycja zostanie ogłoszona jako przetarg, automatycznie się tu pokaże.
              </div>
            )}

            {candidates?.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">
                  Kandydaci (heurystyczny matching: NIP / miasto / fuzzy title):
                </div>
                {candidates.map((c) => (
                  <div key={c.source_id} className="border border-border rounded-md p-3 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{c.source?.toUpperCase()}</Badge>
                      <Badge className="bg-amber-500/15 text-amber-300 text-[10px]">
                        {Math.round((c.confidence || 0) * 100)}% match
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">{c.match_reason}</span>
                    </div>
                    <div className="text-sm font-medium">{c.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-3">
                      {c.city && <span><MapPin className="w-3 h-3 inline mr-0.5" />{c.city}</span>}
                      {c.deadline && <span><Calendar className="w-3 h-3 inline mr-0.5" />{fmtDate(c.deadline)}</span>}
                      {c.estimated_value > 0 && <span>{fmtPLN(c.estimated_value)}</span>}
                    </div>
                    <Button
                      onClick={() => link(c.source_id)}
                      disabled={linking}
                      size="sm"
                      variant="outline"
                      className="gap-1 mt-1"
                    >
                      <ArrowRight className="w-3 h-3" />
                      Połącz z tym tenderem
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ============================================================
// Stage selector (header)
// ============================================================

function StageSelector({ currentStage, onChange, disabled }) {
  return (
    <Select value={currentStage || 'discovered'} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-[200px] h-8">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STAGES.map((s) => (
          <SelectItem key={s} value={s}>{STAGE_LABELS[s].label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


// ============================================================
// Main
// ============================================================

export default function InvestmentDetail({ lang = 'pl', tenantId }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingStage, setChangingStage] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getInvestment(id);
      setData(r);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nie znaleziono inwestycji');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleStageChange(newStage) {
    setChangingStage(true);
    try {
      await changeInvestmentStage(id, newStage, null);
      toast.success(`Etap zmieniony na: ${STAGE_LABELS[newStage]?.label}`);
      load();
    } catch (err) {
      toast.error('Błąd zmiany etapu');
    } finally {
      setChangingStage(false);
    }
  }

  if (loading || !data) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const inv = data.investment;
  const cfg = STAGE_LABELS[inv.investment_stage || 'discovered'];

  return (
    <PlanLock feature="investments_module" requiredPlan="pro" lang={lang}>
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/investments')}
        className="gap-1"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Powrót do listy
      </Button>

      {/* Header */}
      <Card>
        <CardContent className="pt-6 pb-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Badge variant="outline" className={`gap-1 ${cfg?.color || ''}`}>
                  {STAGE_LABELS[inv.investment_stage || 'discovered'].label}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {inv.source === 'gunb' ? '🏛 GUNB' : '✋ Manual'}
                </Badge>
                {inv.match_score >= 70 && (
                  <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">
                    match {inv.match_score}
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-primary" />
                {inv.title}
              </h1>
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mt-2">
                {inv.investor_name && (
                  <span className="flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5" />
                    {inv.investor_name}
                  </span>
                )}
                {inv.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {inv.city}{inv.region ? `, ${inv.region}` : ''}
                  </span>
                )}
                {inv.permit_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Pozwolenie: {fmtDate(inv.permit_date)}
                  </span>
                )}
                {inv.estimated_value > 0 && (
                  <span>Wartość całk.: {fmtPLN(inv.estimated_value)}</span>
                )}
                {inv.investment_value_estimate_pln > 0 && (
                  <span className="text-primary">
                    Wartość oświetlenia: ~{fmtPLN(inv.investment_value_estimate_pln)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-xs text-muted-foreground">Etap pipeline</div>
              <StageSelector
                currentStage={inv.investment_stage}
                onChange={handleStageChange}
                disabled={changingStage}
              />
            </div>
          </div>

          {inv.description && (
            <p className="text-sm text-muted-foreground mt-3 pt-3 border-t">
              {inv.description}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-3">
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="overview">📋 Przegląd</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">
            📡 Aktywność
          </TabsTrigger>
          <TabsTrigger value="outreach" data-testid="tab-outreach">
            📨 Outreach
            {data.outreach_log?.length > 0 && (
              <Badge variant="outline" className="ml-1 text-[10px] h-4 px-1">
                {data.outreach_log.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="spec-in" data-testid="tab-spec-in">
            📐 Spec-in
            {inv.specced_products?.length > 0 && (
              <Badge variant="outline" className="ml-1 text-[10px] h-4 px-1">
                {inv.specced_products.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="linked">🔗 Przetarg</TabsTrigger>
          <TabsTrigger value="history">📜 Historia</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <DecisionChainPanel tenderId={id} />
        </TabsContent>

        <TabsContent value="activity">
          <Card>
            <CardContent className="p-3">
              <ActivityFeed itemType="investment" itemId={id} limit={60} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="outreach">
          <OutreachTab
            investmentId={id}
            outreach={data.outreach_log}
            onChanged={load}
            lang={lang}
          />
        </TabsContent>

        <TabsContent value="spec-in">
          <SpecInTab
            investmentId={id}
            specced={inv.specced_products}
            onChanged={load}
            lang={lang}
          />
        </TabsContent>

        <TabsContent value="linked">
          <LinkedTenderTab
            investmentId={id}
            linkedTender={data.linked_tender}
            onChanged={load}
            lang={lang}
          />
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Historia etapów</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-2">
              {inv.investment_stage_history ? (
                Object.entries(inv.investment_stage_history).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between border-b border-border pb-2">
                    <span>{key.replace('_at', '')}</span>
                    <span className="text-muted-foreground">{fmtDateTime(value)}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground">Brak historii etapów</p>
              )}
              <div className="pt-2 text-[10px] text-muted-foreground">
                Utworzono: {fmtDateTime(inv.created_at)} ·
                Ostatnia aktualizacja: {fmtDateTime(inv.updated_at)}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </PlanLock>
  );
}
